import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI, healthAPI, supportAPI, reportsAPI } from '../utils/api';
import HealthScoreCard from '../components/HealthScoreCard';
import { useLocationScope, LocationScopeSelect } from '../components/LocationScope';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  BarChart3, TrendingUp, IndianRupee, CheckCircle2, Clock, Package,
  AlertTriangle, Users, Loader2, ClipboardCheck, ArrowUpRight,
  Wallet, Banknote, Activity, Receipt, Calculator, MapPin, Download
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar
} from 'recharts';
import { toast } from 'sonner';
import { generateCeoReportPDF } from '../utils/ceoReportPDF';
import { DateRangePicker, rangeLabel } from '../components/DateRangePicker';
import Can from '../components/Can';

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];
const STATUS_COLORS = { draft: '#f59e0b', submitted: '#3b82f6', approved: '#10b981', rejected: '#ef4444', completed: '#059669', deletion_requested: '#f97316' };

function KpiCard({ title, value, icon: Icon, color = 'emerald', subtitle, onClick }) {
  return (
    <Card className={`border-slate-200 hover:shadow-md transition-shadow ${onClick ? 'cursor-pointer' : ''}`} onClick={onClick} data-testid={`kpi-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <p className="text-xs font-medium uppercase tracking-wider text-slate-400">{title}</p>
            <p className={`text-2xl font-bold font-['Outfit'] text-${color}-600`}>{value}</p>
            {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
          </div>
          <div className={`p-2.5 bg-${color}-50 rounded-xl`}>
            <Icon className={`h-5 w-5 text-${color}-600`} />
          </div>
        </div>
        {onClick && <div className="mt-2 flex items-center gap-1 text-xs text-slate-400"><ArrowUpRight className="h-3 w-3" />View details</div>}
      </CardContent>
    </Card>
  );
}

export default function CeoDashboard({ embedded = false }) {
  const navigate = useNavigate();
  const locScope = useLocationScope('ceo_location_scope');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sparkline, setSparkline] = useState([]);

  const [supportSnapshot, setSupportSnapshot] = useState(null);
  const [range, setRange] = useState({ preset: 'all' });
  const fetchData = useCallback(async () => {
    try {
      const [dashRes, histRes, supRes] = await Promise.all([
        dashboardAPI.getCeo({ ...(locScope.locationId ? { location_id: locScope.locationId } : {}), ...(range.from ? { date_from: range.from } : {}), ...(range.to ? { date_to: range.to } : {}) }),
        healthAPI.getHistory(6).catch(() => ({ data: [] })),
        supportAPI.dashboard().catch(() => ({ data: null }))
      ]);
      setData(dashRes.data);
      setSparkline((histRes.data || []).map(s => ({ month: s.month, score: s.score })));
      setSupportSnapshot(supRes.data);
    } catch (err) {
      console.error('Failed to load CEO dashboard:', err);
    } finally {
      setLoading(false);
    }
  }, [locScope.locationId, range.from, range.to]);

  const handleSnapshot = async () => {
    try {
      await healthAPI.snapshot();
      const h = await healthAPI.getHistory(6);
      setSparkline((h.data || []).map(s => ({ month: s.month, score: s.score })));
    } catch { /* ignore */ }
  };

  const [exporting, setExporting] = useState(false);
  const handleExportPdf = async () => {
    if (!data || exporting) return;
    setExporting(true);
    try {
      // Same state the cards render from — the PDF mirrors the screen, no re-aggregation.
      await generateCeoReportPDF({ data, support: supportSnapshot, sparkline, locationLabel: locScope.locationLabel, periodLabel: rangeLabel(range) });
      toast.success('CEO Report downloaded');
    } catch (err) {
      console.error(err);
      toast.error('Could not generate the CEO Report');
    } finally { setExporting(false); }
  };

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div className={`${embedded ? 'py-24' : 'min-h-screen'} bg-slate-50 flex items-center justify-center`}><Loader2 className="h-8 w-8 animate-spin text-emerald-600" /></div>;
  if (!data) return <div className={`${embedded ? 'py-24' : 'min-h-screen'} bg-slate-50 flex flex-col items-center justify-center gap-3`}><p className="text-slate-500">Couldn't load the business numbers.</p><Button variant="outline" size="sm" onClick={fetchData}>Try again</Button></div>;

  const { kpis, status_distribution, revenue_trend, sales_funnel, top_staff, accounts_summary, readings_summary } = data;
  const cash = accounts_summary?.cash_on_hand;
  const balance = accounts_summary?.account_balance;
  const opExpMtd = accounts_summary?.operational_expense_mtd || 0;
  const gstInputMtd = accounts_summary?.gst_input_mtd || 0;
  const cashHistory = accounts_summary?.cash_history || [];

  const funnelData = [
    { name: 'Leads', value: sales_funnel.total_leads, fill: '#94a3b8' },
    { name: 'Quotes', value: sales_funnel.quotes_generated, fill: '#3b82f6' },
    { name: 'Approved', value: sales_funnel.approved, fill: '#10b981' },
    { name: 'Completed', value: sales_funnel.completed, fill: '#059669' }
  ];

  return (
    <div className={embedded ? '' : 'min-h-screen bg-slate-50 py-6 px-4'}>
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-2 sm:gap-3 mb-6 flex-wrap">
          <div className={embedded ? 'w-full sm:w-auto sm:flex-1' : 'flex-1'}>
            {embedded
              ? <p className="text-sm text-slate-500">Revenue, profit, cash and the Company Health Score for the period you pick.</p>
              : <><h1 className="text-2xl font-bold font-['Outfit'] text-slate-900" data-testid="ceo-title">Business health</h1>
                <p className="text-sm text-slate-500">High-level business overview</p></>}
          </div>
          <DateRangePicker value={range} onChange={setRange} />
          <div className="w-full sm:w-48"><LocationScopeSelect scope={locScope} testIdPrefix="ceo-location" /></div>
          <Can module="module_ceo_dashboard" action="export"><Button onClick={handleExportPdf} disabled={exporting} className="gap-2 bg-slate-900 hover:bg-slate-800 text-white" data-testid="ceo-report-download-btn">{exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}Download report PDF</Button></Can>
          <Button variant="outline" onClick={() => navigate('/dashboard/reports')} className="gap-2" data-testid="goto-reports-btn"><BarChart3 className="h-4 w-4" />All reports</Button>
        </div>

        {/* Company Health Score — hero */}
        {data.health_score && (
          <div className="mb-6">
            <HealthScoreCard
              health={data.health_score}
              sparkline={sparkline}
              onSnapshot={handleSnapshot}
              onPillarClick={(key) => {
                const map = { profitability: '/dashboard/alerts', cash_collections: '/dashboard/credits',
                              operations: '/dashboard/inventory', sales_growth: '/dashboard/reports?type=sales_revenue' };
                if (map[key]) navigate(map[key]);
              }}
            />
          </div>
        )}

        {/* KPI Grid — symmetric 4×2 */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="kpi-grid">
          <KpiCard title="Total Revenue" value={`₹${(kpis.total_revenue || 0).toLocaleString('en-IN')}`} icon={IndianRupee} color="emerald" subtitle="Approved + Completed" onClick={() => navigate('/dashboard/reports?type=sales_revenue')} />
          <KpiCard title="Total Profit" value={`₹${(kpis.total_profit || 0).toLocaleString('en-IN')}`} icon={TrendingUp} color="blue" subtitle="Internal margins" onClick={() => navigate('/dashboard/reports?type=profit_leakage')} />
          <KpiCard title="Conversion Rate" value={`${kpis.conversion_rate}%`} icon={BarChart3} color="violet" subtitle={`${kpis.wins || 0} won of ${kpis.total_projects} leads`} />
          <KpiCard title="Active Projects" value={kpis.active_projects} icon={Clock} color="amber" onClick={() => navigate('/dashboard/projects')} />
          <KpiCard title="Completed" value={kpis.completed_projects} icon={CheckCircle2} color="emerald" onClick={() => navigate('/dashboard/reports?type=project_execution')} />
          <KpiCard title="Pending Approvals" value={kpis.pending_approvals} icon={ClipboardCheck} color="red" onClick={() => navigate('/dashboard/approvals')} />
          <KpiCard title="Inventory Value" value={`₹${(kpis.inventory_value || 0).toLocaleString('en-IN')}`} icon={Package} color="slate" onClick={() => navigate('/dashboard/reports?type=inventory_material')} />
          <KpiCard title="Low Stock Alerts" value={kpis.low_stock_alerts} icon={AlertTriangle} color="amber" onClick={() => navigate('/dashboard/inventory')} />
        </div>

        {/* Revenue breakdown by channel — kept as separate identifiable lines, never blended into one number */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6" data-testid="revenue-breakdown-grid">
          <Card className="border-slate-200" data-testid="revenue-project-card">
            <CardContent className="p-4">
              <p className="text-xs text-slate-500">Project Revenue</p>
              <p className="text-xl font-bold font-['Outfit'] text-slate-900">₹{(data?.project_revenue || 0).toLocaleString('en-IN')}</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200 cursor-pointer hover:border-blue-300" onClick={() => navigate('/dashboard/sales')} data-testid="revenue-direct-sales-card">
            <CardContent className="p-4">
              <p className="text-xs text-slate-500">Counter Sale Revenue</p>
              <p className="text-xl font-bold font-['Outfit'] text-slate-900">₹{(data?.direct_sales?.revenue || 0).toLocaleString('en-IN')}</p>
              <p className="text-[11px] text-slate-400 mt-1">{data?.direct_sales?.count || 0} sales · margin ₹{(data?.direct_sales?.margin || 0).toLocaleString('en-IN')}</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200 cursor-pointer hover:border-emerald-300" onClick={() => navigate('/dashboard/ecommerce')} data-testid="revenue-ecommerce-card">
            <CardContent className="p-4">
              <p className="text-xs text-slate-500">Ecommerce Revenue</p>
              <p className="text-xl font-bold font-['Outfit'] text-slate-900">₹{(data?.ecommerce?.revenue || 0).toLocaleString('en-IN')}</p>
              <p className="text-[11px] text-slate-400 mt-1">{data?.ecommerce?.count || 0} orders · commission ₹{(data?.ecommerce?.commission || 0).toLocaleString('en-IN')}</p>
            </CardContent>
          </Card>
        </div>

        {/* Customer Support snapshot (Iter 47) */}
        {supportSnapshot && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6" data-testid="ceo-support-snapshot">
            <Card className="border-slate-200 cursor-pointer" onClick={() => navigate('/dashboard/amc')} data-testid="ceo-support-open">
              <CardContent className="p-4"><p className="text-xs text-slate-500">Open Tickets</p><p className="text-xl font-bold text-slate-900">{supportSnapshot.open_tickets}</p></CardContent>
            </Card>
            <Card className={`border-slate-200 cursor-pointer ${supportSnapshot.overdue_by_sla ? 'border-red-200 bg-red-50/40' : ''}`} onClick={() => navigate('/dashboard/amc')} data-testid="ceo-support-overdue">
              <CardContent className="p-4"><p className="text-xs text-slate-500">Overdue (SLA breach)</p><p className={`text-xl font-bold ${supportSnapshot.overdue_by_sla ? 'text-red-600' : 'text-slate-900'}`}>{supportSnapshot.overdue_by_sla}</p></CardContent>
            </Card>
            <Card className="border-slate-200" data-testid="ceo-support-avgres">
              <CardContent className="p-4"><p className="text-xs text-slate-500">Avg Resolution</p><p className="text-xl font-bold text-slate-900">{supportSnapshot.avg_resolution_hours}h</p></CardContent>
            </Card>
            <Card className="border-slate-200" data-testid="ceo-support-csat">
              <CardContent className="p-4"><p className="text-xs text-slate-500">Avg CSAT</p><p className="text-xl font-bold text-emerald-700">{supportSnapshot.avg_csat ? `${supportSnapshot.avg_csat}/5` : '—'}</p></CardContent>
            </Card>
          </div>
        )}

        {/* Operational Snapshot — Cash · Op Exp · GST Input · Account Balance */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="ceo-snapshot-grid">
          {/* Cash on Hand */}
          <Card className="border-emerald-200 bg-emerald-50/40 hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/dashboard/credits')} data-testid="ceo-cash-card">
            <CardHeader className="pb-2"><div className="flex items-center justify-between"><CardTitle className="text-sm font-semibold text-emerald-700 flex items-center gap-2"><Wallet className="h-4 w-4" />Cash on Hand</CardTitle><ArrowUpRight className="h-3.5 w-3.5 text-emerald-500" /></div></CardHeader>
            <CardContent>
              <p className="text-2xl font-bold font-['Outfit'] text-slate-900">₹{(cash?.amount || 0).toLocaleString('en-IN')}</p>
              <p className="text-[11px] text-slate-500 mt-1">{cash?.entry_date ? `As of ${cash.entry_date}` : 'No entries yet'}</p>
              {cashHistory.length >= 2 && (
                <div className="mt-3 h-12 w-full">
                  <ResponsiveContainer width="100%" height={48}>
                    <LineChart data={cashHistory} margin={{ top: 2, right: 4, bottom: 2, left: 4 }}><Line type="monotone" dataKey="amount" stroke="#10b981" strokeWidth={2} dot={false} isAnimationActive={false} /><Tooltip formatter={(v) => `₹${v.toLocaleString('en-IN')}`} labelFormatter={(l) => l} /></LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Operational Expense MTD */}
          <Card className="border-amber-200 bg-amber-50/40 hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/dashboard/credits')} data-testid="ceo-opexp-card">
            <CardHeader className="pb-2"><div className="flex items-center justify-between"><CardTitle className="text-sm font-semibold text-amber-700 flex items-center gap-2"><Receipt className="h-4 w-4" />Op Exp (MTD)</CardTitle><ArrowUpRight className="h-3.5 w-3.5 text-amber-500" /></div></CardHeader>
            <CardContent>
              <p className="text-2xl font-bold font-['Outfit'] text-slate-900">₹{opExpMtd.toLocaleString('en-IN')}</p>
              <p className="text-[11px] text-slate-500 mt-1">Operational outflow this month</p>
            </CardContent>
          </Card>

          {/* GST Input MTD */}
          <Card className="border-sky-200 bg-sky-50/40 hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/dashboard/credits')} data-testid="ceo-gst-card">
            <CardHeader className="pb-2"><div className="flex items-center justify-between"><CardTitle className="text-sm font-semibold text-sky-700 flex items-center gap-2"><Calculator className="h-4 w-4" />GST Input (MTD)</CardTitle><ArrowUpRight className="h-3.5 w-3.5 text-sky-500" /></div></CardHeader>
            <CardContent>
              <p className="text-2xl font-bold font-['Outfit'] text-slate-900">₹{gstInputMtd.toLocaleString('en-IN')}</p>
              <p className="text-[11px] text-slate-500 mt-1">Input tax credits this month</p>
            </CardContent>
          </Card>

          {/* Account Balance */}
          <Card className="border-violet-200 bg-violet-50/40 hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/dashboard/credits')} data-testid="ceo-balance-card">
            <CardHeader className="pb-2"><div className="flex items-center justify-between"><CardTitle className="text-sm font-semibold text-violet-700 flex items-center gap-2"><Banknote className="h-4 w-4" />Account Balance</CardTitle><ArrowUpRight className="h-3.5 w-3.5 text-violet-500" /></div></CardHeader>
            <CardContent>
              <p className="text-2xl font-bold font-['Outfit'] text-slate-900">₹{(balance?.amount || 0).toLocaleString('en-IN')}</p>
              <p className="text-[11px] text-slate-500 mt-1">{balance?.entry_date ? `As of ${balance.entry_date}` : 'No entries yet'}</p>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row — Revenue (2) + Status (1) + Readings (1) = symmetric 4-col */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Revenue Trend */}
          <Card className="border-slate-200 lg:col-span-2" data-testid="revenue-chart">
            <CardHeader className="pb-2"><CardTitle className="text-base font-['Outfit']">Revenue Trend</CardTitle></CardHeader>
            <CardContent className="p-4 pt-0">
              {revenue_trend.length > 0 ? (
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={revenue_trend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                    <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={(v) => v >= 100000 ? `${(v/100000).toFixed(1)}L` : v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} />
                    <Tooltip formatter={(v) => [`₹${v.toLocaleString('en-IN')}`, 'Revenue']} />
                    <Line type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2.5} dot={{ r: 4, fill: '#10b981' }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : <p className="text-sm text-slate-400 text-center py-16">No revenue data yet</p>}
            </CardContent>
          </Card>

          {/* Status Distribution */}
          <Card className="border-slate-200" data-testid="status-chart">
            <CardHeader className="pb-2"><CardTitle className="text-base font-['Outfit']">Project Status</CardTitle></CardHeader>
            <CardContent className="p-4 pt-0">
              {status_distribution.length > 0 ? (
                <div className="space-y-3 pt-2">
                  {status_distribution.map((item) => {
                    const total = status_distribution.reduce((s, i) => s + i.value, 0);
                    const pct = total > 0 ? (item.value / total) * 100 : 0;
                    const color = STATUS_COLORS[item.name] || '#94a3b8';
                    return (
                      <div key={item.name} className="space-y-1">
                        <div className="flex justify-between text-xs"><span className="text-slate-600 capitalize">{item.name}</span><span className="font-bold" style={{color}}>{item.value} ({pct.toFixed(0)}%)</span></div>
                        <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden"><div className="h-full rounded-full" style={{width: `${pct}%`, backgroundColor: color}} /></div>
                      </div>
                    );
                  })}
                </div>
              ) : <p className="text-sm text-slate-400 text-center py-16">No projects yet</p>}
            </CardContent>
          </Card>

          {/* Readings */}
          <Card className="border-blue-200 bg-blue-50/40 hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/dashboard/readings')} data-testid="ceo-readings-card">
            <CardHeader className="pb-2"><div className="flex items-center justify-between"><CardTitle className="text-sm font-semibold text-blue-700 flex items-center gap-2"><Activity className="h-4 w-4" />Readings</CardTitle><ArrowUpRight className="h-3.5 w-3.5 text-blue-500" /></div></CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-3xl font-bold font-['Outfit'] text-slate-900">{readings_summary?.active || 0}</p>
                <p className="text-[11px] text-slate-500 mt-1">Active readings</p>
              </div>
              <div className="space-y-1.5 pt-2 border-t border-blue-100">
                <div className="flex justify-between text-xs"><span className="text-slate-600">Completed</span><span className="font-semibold text-emerald-700">{readings_summary?.completed || 0}</span></div>
                <div className="flex justify-between text-xs"><span className="text-slate-600">Overdue</span><span className="font-semibold text-red-700">{readings_summary?.overdue || 0}</span></div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Bottom Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Sales Funnel */}
          <Card className="border-slate-200" data-testid="funnel-chart">
            <CardHeader className="pb-2"><CardTitle className="text-base font-['Outfit']">Sales Funnel</CardTitle></CardHeader>
            <CardContent className="p-4 pt-0">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={funnelData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} stroke="#94a3b8" width={80} />
                  <Tooltip formatter={(v) => [v, 'Count']} />
                  <Bar dataKey="value" radius={[0, 6, 6, 0]} fill="#3b82f6" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Top Staff */}
          <Card className="border-slate-200" data-testid="top-staff">
            <CardHeader className="pb-2"><CardTitle className="text-base font-['Outfit'] flex items-center gap-2"><Users className="h-4 w-4" />Top Performing Staff</CardTitle></CardHeader>
            <CardContent className="p-4 pt-0">
              {top_staff.length > 0 ? (
                <div className="space-y-3">
                  {top_staff.map((s, idx) => (
                    <div key={s.name || idx} className="flex items-center gap-3">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white ${idx === 0 ? 'bg-amber-500' : idx === 1 ? 'bg-slate-400' : 'bg-orange-300'}`}>{idx + 1}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{s.name}</p>
                        <p className="text-xs text-slate-500">{s.count} projects</p>
                      </div>
                      <Badge variant="outline" className="text-xs shrink-0">₹{s.revenue.toLocaleString('en-IN')}</Badge>
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-slate-400 text-center py-8">No staff data</p>}
            </CardContent>
          </Card>
        </div>

        {/* Top Performing Locations — consolidated view only (Iter 50 §3) */}
        {Array.isArray(data.top_locations) && (
          <Card className="border-slate-200 mt-4" data-testid="top-locations">
            <CardHeader className="pb-2"><CardTitle className="text-base font-['Outfit'] flex items-center gap-2"><MapPin className="h-4 w-4" />Top Performing Locations</CardTitle></CardHeader>
            <CardContent className="p-4 pt-0">
              {data.top_locations.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-xs text-slate-500 border-b"><th className="py-2 pr-3">#</th><th className="py-2 pr-3">Location</th><th className="py-2 pr-3">Revenue</th><th className="py-2 pr-3">Margin</th><th className="py-2 pr-3">This Month</th><th className="py-2 pr-3">MoM</th></tr></thead>
                    <tbody>
                      {data.top_locations.map(l => (
                        <tr key={l.location_id} className="border-b last:border-0" data-testid={`top-location-row-${l.location_id}`}>
                          <td className="py-2 pr-3"><span className={`w-6 h-6 rounded-full inline-flex items-center justify-center text-xs font-bold text-white ${l.rank === 1 ? 'bg-amber-500' : l.rank === 2 ? 'bg-slate-400' : l.rank === 3 ? 'bg-orange-300' : 'bg-slate-200 text-slate-600'}`}>{l.rank}</span></td>
                          <td className="py-2 pr-3 font-medium text-slate-900">{l.name}<span className="block text-[11px] text-slate-400 font-normal">{l.wins} won of {l.projects}</span></td>
                          <td className="py-2 pr-3">₹{l.revenue.toLocaleString('en-IN')}</td>
                          <td className="py-2 pr-3">₹{l.margin.toLocaleString('en-IN')} <span className="text-[11px] text-slate-400">({l.margin_pct}%)</span></td>
                          <td className="py-2 pr-3">₹{l.this_month.toLocaleString('en-IN')}</td>
                          <td className="py-2 pr-3">{l.mom_pct == null ? <span className="text-slate-400">—</span> : <span className={l.mom_pct >= 0 ? 'text-emerald-600' : 'text-red-600'}>{l.mom_pct >= 0 ? '+' : ''}{l.mom_pct}%</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <p className="text-sm text-slate-400 text-center py-6">No location-attributed projects yet — assign projects to a location to rank branches.</p>}
            </CardContent>
          </Card>
        )}

        {/* Credit Section */}
        {data.credit_data && (
          <Card className="border-slate-200 mt-4" data-testid="credit-section">
            <CardHeader className="pb-2"><CardTitle className="text-base font-['Outfit'] flex items-center gap-2"><IndianRupee className="h-4 w-4" />Customer Credit Overview</CardTitle></CardHeader>
            <CardContent className="p-4 pt-0">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Aging Chart */}
                <div className="space-y-3">
                  <p className="text-xs font-medium uppercase tracking-wider text-slate-400">Credit Aging</p>
                  {[
                    { label: '0-30 Days', value: data.credit_data.aging?.['0_30'] || 0, color: '#10b981' },
                    { label: '30-60 Days', value: data.credit_data.aging?.['30_60'] || 0, color: '#f59e0b' },
                    { label: '60+ Days', value: data.credit_data.aging?.['60_plus'] || 0, color: '#ef4444' }
                  ].map(bucket => {
                    const maxVal = Math.max(data.credit_data.aging?.['0_30'] || 0, data.credit_data.aging?.['30_60'] || 0, data.credit_data.aging?.['60_plus'] || 0, 1);
                    return (
                      <div key={bucket.label} className="space-y-1">
                        <div className="flex justify-between text-xs"><span className="text-slate-600">{bucket.label}</span><span className="font-bold">₹{bucket.value.toLocaleString('en-IN')}</span></div>
                        <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden"><div className="h-full rounded-full" style={{width: `${(bucket.value / maxVal) * 100}%`, backgroundColor: bucket.color}} /></div>
                      </div>
                    );
                  })}
                </div>
                {/* Top Debtors */}
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-3">Top 5 Outstanding</p>
                  {data.credit_data.top_debtors?.length > 0 ? (
                    <div className="space-y-2">
                      {data.credit_data.top_debtors.map((d, i) => (
                        <div key={d.name || i} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                          <span className="text-sm text-slate-700">{d.name}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-bold text-slate-900">₹{d.balance.toLocaleString('en-IN')}</span>
                            {d.status === 'overdue' && <Badge className="bg-red-100 text-red-700 text-[9px]">Overdue</Badge>}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : <p className="text-sm text-slate-400 text-center py-4">No outstanding credits</p>}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
